import React, { useRef, useState } from "react";
import "./contact.css";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

const WHATSAPP_NUMBER = "6285717486916";
const EMAIL_ADDRESS = "adityarahman453@gmail.com";

const waLink = (text) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

const Contact = () => {
  const form = useRef();
  const [sending, setSending] = useState(false);

  const sendEmail = async (e) => {
    e.preventDefault();
    if (sending) return;

    const data = new FormData(form.current);
    const name = (data.get("name") || "").trim();
    const email = (data.get("email") || "").trim();
    const project = (data.get("project") || "").trim();

    // Jaring pengaman untuk isian yang cuma spasi; `required` menangani sisanya.
    if (!name || !email || !project) {
      toast.warn("Please fill in your name, email, and a short description.");
      return;
    }

    setSending(true);

    // Kiriman form berhenti di server sendiri (dityaraa.my.id/api/lead): lead
    // disimpan ke database lalu diteruskan ke email. Form sengaja TIDAK lagi
    // membuka WhatsApp — kartu WhatsApp di sebelah tetap ada untuk yang memang
    // ingin memakainya. Karena tak ada lagi window.open yang harus menumpang
    // gesture klik, alurnya boleh await seperti biasa.
    try {
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          message: project,
          website: (data.get("website") || "").toString(), // honeypot
        }),
      });
      const body = await res.json();
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);

      toast.success("Message sent — I'll get back to you shortly.", { autoClose: 4000 });
      form.current.reset();
    } catch (err) {
      // Tanpa jalur WhatsApp, gagal di sini berarti pesannya benar-benar tidak
      // sampai — jadi alamat emailnya disebut supaya pengunjung tetap punya jalan.
      console.error("Lead submit failed:", err);
      toast.error(`Couldn't send. Please email me at ${EMAIL_ADDRESS}.`, { autoClose: 6000 });
    } finally {
      setSending(false);
    }
  };

  const openSocialMedia = (item) => {
    if (item === "email") {
      window.open(`mailto:${EMAIL_ADDRESS}?subject=${encodeURIComponent("Project enquiry from dityaraa.my.id")}`, "_blank");
    } else if (item === "whatsapp") {
      window.open(waLink("Hi Adit, I found you through dityaraa.my.id."), "_blank", "noopener,noreferrer");
    }
  };

  const variants = {
    visible: {
      opacity: 1,
      x: 0,
    },
    hidden: {
      opacity: 0,
      x: -200,
    },
  };

  const scrollVariants = {
    initial: {
      x: -3,
    },
    scrollButton: {
      x: 7,
      transition: {
        duration: 1.4,
        repeat: Infinity,
      },
    },
  };

  return (
    <section className="contact section" id="contact">
      <h2 className="section__title">Get in touch</h2>
      <span className="section__subtitle">Contact Me</span>

      <div className="contact__container container grid">
        <div className="contact__content">
          <h3 className="contact__title">Talk to me</h3>

          <div className="contact__info">
            <div className="contact__card">
              <motion.i
                variants={variants}
                initial="hidden"
                whileInView="visible"
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                className="bx bx-mail-send contact__card-icon"
              ></motion.i>

              <h3 className="contact__card-title">Email</h3>
              <span className="contact__card-data">{EMAIL_ADDRESS}</span>
              <div className="contact__button" onClick={() => openSocialMedia("email")}>
                Write me{" "}
                <motion.i
                  variants={scrollVariants}
                  initial="initial"
                  animate="scrollButton"
                  className="bx bx-right-arrow-alt contact__button-icon"
                ></motion.i>
              </div>
            </div>

            <div className="contact__card">
              <motion.i
                variants={variants}
                initial="hidden"
                whileInView="visible"
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                className="bx bxl-whatsapp contact__card-icon"
              ></motion.i>

              <h3 className="contact__card-title">Whatsapp</h3>
              <span className="contact__card-data">+{WHATSAPP_NUMBER}</span>
              <div className="contact__button" onClick={() => openSocialMedia("whatsapp")}>
                Write me{" "}
                <motion.i
                  variants={scrollVariants}
                  initial="initial"
                  animate="scrollButton"
                  className="bx bx-right-arrow-alt contact__button-icon"
                ></motion.i>
              </div>
            </div>

            <div className="contact__card">
              <motion.i
                variants={variants}
                initial="hidden"
                whileInView="visible"
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                className="bx bxl-instagram contact__card-icon"
              ></motion.i>

              <h3 className="contact__card-title">Instagram</h3>
              <span className="contact__card-data">_dityaraa.21</span>
              <a href="https://www.instagram.com/_dityaraa.21/" className="contact__button">
                Write me{" "}
                <motion.i
                  variants={scrollVariants}
                  initial="initial"
                  animate="scrollButton"
                  className="bx bx-right-arrow-alt contact__button-icon"
                ></motion.i>
              </a>
            </div>
          </div>
        </div>

        <div className="contact__content">
          <h3 className="contact__title">Write me your project</h3>

          <form ref={form} onSubmit={sendEmail} className="contact__form">
            {/* Honeypot: disembunyikan dari manusia lewat CSS, jadi hanya bot
                yang mengisinya. Server membuang kiriman yang field ini terisi.
                Pakai off-screen, bukan display:none — sebagian bot melewati
                field yang display:none. */}
            <input
              type="text"
              name="website"
              className="contact__honeypot"
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
            />

            <div className="contact__form-div">
              <label className="contact__form-tag">Name</label>
              <input type="text" name="name" className="contact__form-input" placeholder="Insert your name" required />
            </div>

            <div className="contact__form-div">
              <label className="contact__form-tag">Mail</label>
              <input type="email" name="email" className="contact__form-input" placeholder="Insert your email" required />
            </div>

            <div className="contact__form-div contact__form-area">
              <label className="contact__form-tag">Description</label>
              <textarea name="project" cols="30" rows="10" className="contact__form-input" placeholder="Tell me what you need — the more detail, the better." required></textarea>
            </div>

            <button className="button button--flex" disabled={sending}>
              {sending ? "Sending..." : "Send Message"}
              <svg className="button__icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M14.2199 21.9352C13.0399 21.9352 11.3699 21.1052 10.0499 17.1352L9.32988 14.9752L7.16988 14.2552C3.20988 12.9352 2.37988 11.2652 2.37988 10.0852C2.37988 8.91525 3.20988 7.23525 7.16988 5.90525L15.6599 3.07525C17.7799 2.36525 19.5499 2.57525 20.6399 3.65525C21.7299 4.73525 21.9399 6.51525 21.2299 8.63525L18.3999 17.1252C17.0699 21.1052 15.3999 21.9352 14.2199 21.9352ZM7.63988 7.33525C4.85988 8.26525 3.86988 9.36525 3.86988 10.0852C3.86988 10.8052 4.85988 11.9052 7.63988 12.8252L10.1599 13.6652C10.3799 13.7352 10.5599 13.9152 10.6299 14.1352L11.4699 16.6552C12.3899 19.4352 13.4999 20.4252 14.2199 20.4252C14.9399 20.4252 16.0399 19.4352 16.9699 16.6552L19.7999 8.16525C20.3099 6.62525 20.2199 5.36525 19.5699 4.71525C18.9199 4.06525 17.6599 3.98525 16.1299 4.49525L7.63988 7.33525Z"
                  fill="var(--container-color)"
                ></path>
                <path
                  d="M10.11 14.7052C9.92005 14.7052 9.73005 14.6352 9.58005 14.4852C9.29005 14.1952 9.29005 13.7152 9.58005 13.4252L13.16 9.83518C13.45 9.54518 13.93 9.54518 14.22 9.83518C14.51 10.1252 14.51 10.6052 14.22 10.8952L10.64 14.4852C10.5 14.6352 10.3 14.7052 10.11 14.7052Z"
                  fill="var(--container-color)"
                ></path>
              </svg>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
